import React, { useEffect } from 'react';

// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { assign, set, has, find } from 'lodash';
import { connect } from 'react-redux';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';
import COLResultsPanel from '../../components/cost-of-living-calculator/compare-cities/results-panel/col-results-panel';

// Actions
import handleNewParams from '../../lib/col/entry/compare';

// Lib
import { cityChange } from '../../lib/col/col-actions';
import { saveHomeCalculatorProfile } from '../../lib/actions';
import saveToProfile from '../../lib/actions/save-to-profile';

// Local Components
import COLMembershipCta from '../../components/cost-of-living-calculator/shared/col-membership-cta/col-membership-cta';
import Results from '../../components/currency/results/results';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';
import {
  getEventType,
  getEventDetails,
  getEventEntityName,
  getColJoinNerdwallet,
  ENTITY_NAME,
  SECTION_NAME,
} from '../../lib/analytics-constants';

// Configs and Data
import resultsTableConfig from '../../config/col-calculator/results-table';
import toolLinkConfig from '../../config/col-calculator/tool-link-config';
import { useUrlRecordContext } from '../../components/url-record';
import colRelatedArticles from '../../components/cost-of-living-calculator/shared/col-related-articles';
import SegmentImpression from '../../lib/analytics/segment-impression';

export const RELATED_ARTICLES = {
  entityName: ENTITY_NAME.COST_OF_LIVING_RELATED_ARTICLES,
  sectionName: SECTION_NAME.COST_OF_LIVING_RELATED_ARTICLES,
  sectionPosition: 5,
  articles: colRelatedArticles,
};

export interface COLResultsProps {
  costOfLivingCalculator: Record<string, any>;
  dispatch: (p: any) => void;
  history: Record<string, any>;
  identity: Record<string, any>;
  location: Record<string, any>;
  params: Record<string, any>;
  validations: Record<string, any>;
  errors: Record<string, any>;
}

// Lazy Load Components
const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-col-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

// Constants
const basePathname = '/cost-of-living-calculator';

const COLResults = (props: COLResultsProps): JSX.Element => {
  const {
    costOfLivingCalculator,
    dispatch,
    errors,
    history,
    identity,
    params,
    validations,
  } = props;

  useEffect(() => {
    handleNewParams(params, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  const { title, synopsis } = useUrlRecordContext();

  const handleInputChange = (data) => {
    const { cities, inputs } = costOfLivingCalculator;
    let sourceSlug = '';
    let destinationSlug = '';

    if (data?.inputs) {
      if (data.inputs?.destinationCity) {
        const destinationCityData = find(cities, {
          id: data.inputs.destinationCity,
        });
        const sourceCityData = find(cities, {
          id: inputs.sourceCity,
        });
        destinationSlug = destinationCityData.slug;
        sourceSlug = sourceCityData.slug;
      } else if (data.inputs?.sourceCity) {
        const sourceCityData = find(cities, {
          id: data.inputs.sourceCity,
        });
        const destinationCityData = find(cities, {
          id: inputs.destinationCity,
        });
        sourceSlug = sourceCityData.slug;
        if (destinationCityData) {
          destinationSlug = destinationCityData.slug;
        }
      }

      if (sourceSlug && destinationSlug) {
        // go to the results page
        history.push(
          `${basePathname}/compare/${sourceSlug}-vs-${destinationSlug}`
        );
      } else if (
        has(data.inputs, 'destinationCity') &&
        data.inputs?.destinationCity === undefined
      ) {
        // go from results to landing page
        history.push(`${basePathname}`);
      }

      // Don't want to go to results page, but sourceCity or income changed on landing page
      dispatch(saveHomeCalculatorProfile(data.inputs));
      dispatch(saveToProfile());
      dispatch(cityChange(data));
    }
  };

  const onChange = (value, key) => {
    const data = set(
      {
        name: 'costOfLivingCalculator',
      },
      key,
      value
    );
    handleInputChange(data);
  };

  const data = {
    ...costOfLivingCalculator,
    validations: validations || {},
  };

  const resultsPanelProps = {
    data,
    errors,
    onChange,
    handleInputChange,
    resultsTableConfig,
    ...props,
  };

  const getEvent = getColJoinNerdwallet();
  const getEventAttributes = getEventDetails(getEvent);

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name="mortgages_cost_of_living_calculator">
        <Impression
          eventType="page_impression"
          entityName="mortgages_cost_of_living_calculator"
        >
          <SegmentImpression
            entityName="cost_of_living_calculator"
            formName="cost_of_living_calculator"
          >
            <Results
              {...assign({}, props, {
                className: 'col-app',
                data,
                heading: title,
                methodology: (
                  <Methodology
                    trackingFormName="col-methodology"
                    impressionTracking
                  />
                ),
                relatedArticles: (
                  <RelatedArticles
                    entityName={RELATED_ARTICLES.entityName}
                    sectionName={RELATED_ARTICLES.sectionName}
                    sectionPosition={RELATED_ARTICLES.sectionPosition}
                    articles={RELATED_ARTICLES.articles}
                  />
                ),
                membershipCta: (
                  <COLMembershipCta
                    identity={identity}
                    impressionPayload={{
                      eventType: getEventType(getEvent),
                      entityName: getEventEntityName(getEvent),
                      payload: getEventAttributes,
                    }}
                  />
                ),
                // @ts-ignore - ignore error
                resultsPanel: <COLResultsPanel {...resultsPanelProps} />,
                split: '100%',
                subcopy: synopsis,
                toolLinkCards: (
                  <ToolLinkCards
                    header={toolLinkConfig.header}
                    cards={toolLinkConfig.cards}
                    impressionTrackingName={
                      toolLinkConfig.impressionTrackingName
                    }
                    // @ts-ignore - ignore error
                    data={data}
                    calculatorType="col_calculator"
                    analyticsConfig={{
                      sectionName: SECTION_NAME.COST_OF_LIVING_CARD,
                      sectionPosition: 6,
                    }}
                  />
                ),
              })}
            />
          </SegmentImpression>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

COLResults.displayName = 'COLResults';

export default connect((state) => state)(COLResults);
